import { useRef, type RefObject } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import { SpotlightBackdrop } from './SpotlightBackdrop';
import { TourTooltip } from './TourTooltip';
import { useAnchorRect } from '../../hooks/useAnchorRect';
import { useAuth } from '../../hooks/useAuth';
import { useTour } from '../../hooks/useTour';

/**
 * The visible part of the tour. Reads state from TourProvider; renders the
 * spotlight backdrop + Radix Popover (anchored via virtualRef to the live
 * DOMRect of the active anchor). Renders nothing unless status==='active'.
 */
export function TourOverlay() {
  const { status, currentStep, step, anchor, config, next, complete } = useTour();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const rect = useAnchorRect(anchor);

  // Render-time gate re-check. TourProvider clears the active tour via a
  // useEffect, which is asynchronous w.r.t. the URL change. Between the URL
  // changing (e.g. into ?mode=review) and the pause effect firing, the
  // overlay would otherwise render a Popover whose anchor rect is in motion
  // — the resulting composeRefs loop trips React 19's update cap. Checking
  // the gate inside render gives us an immediate bail-out.
  const gateOk =
    !!config &&
    !!user &&
    config.gate.shouldRun({
      user,
      pathname,
      searchParams,
      viewportOk:
        typeof window === 'undefined' || window.innerWidth >= 768,
    });

  // Stable virtualRef. Reading `rect` through a ref means the rect closure
  // always returns the *latest* value without our virtualAnchor object
  // identity changing across renders. The previous `virtualRef={{ current }}`
  // literal created a new ref object every render, which fed into Radix
  // Popover's composeRefs chain — combined with rect-driven re-renders from
  // useAnchorRect's ResizeObserver, the composed state-setter ref would
  // re-fire each frame and trip React 19's max-update-depth cap (#185).
  const rectRef = useRef<DOMRect | null>(rect);
  rectRef.current = rect;
  type Measurable = {
    getBoundingClientRect: () => DOMRect;
    getClientRects: () => DOMRectList;
  };
  const virtualAnchorRef = useRef<{ current: Measurable }>({
    current: {
      getBoundingClientRect: () => rectRef.current as DOMRect,
      getClientRects: () =>
        [rectRef.current as DOMRect] as unknown as DOMRectList,
    },
  });

  if (status !== 'active' || !step || !rect || !config || !gateOk) return null;

  const isLast = currentStep === config.steps.length - 1;
  const handleNext = isLast ? complete : next;

  return (
    <>
      <SpotlightBackdrop rect={rect} />
      <Popover.Root open modal={false}>
        <Popover.Anchor
          virtualRef={
            virtualAnchorRef.current as unknown as RefObject<HTMLElement>
          }
        />
        <Popover.Portal>
          <TourTooltip
            step={step}
            currentIndex={currentStep}
            total={config.steps.length}
            isLast={isLast}
            onNext={handleNext}
            onSkip={complete}
          />
        </Popover.Portal>
      </Popover.Root>
    </>
  );
}
